<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import TagTooltip from "@apps/instagram/components/InstagramPostModal/components/TagTooltip.svelte";
    import { INSTAGRAM_POST_MEDIA_VIDEO_TYPE } from "@apps/instagram/constants";
    import type InstagramPostEntity from "@apps/instagram/entities/InstagramPostEntity";
    import type InstagramPostTagEntity from "@apps/instagram/entities/InstagramPostTagEntity";
    import { createProductInfoObservable } from "@apps/instagram/utils/createProductInfoObservable";
    import ArrowIcon from "@apps/instagram/widgets/instagram_gallery_widget/components/icons/ArrowIcon.svelte";
    import { instagramGalleryWidgetContextToken } from "@apps/instagram/widgets/instagram_gallery_widget/context_tokens";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import Observable from "@ui-utils/Observable.svelte";
    import { createEventDispatcher } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Clearfix from "@components/Clearfix.svelte";
    import Icon from "@components/Icon.svelte";
    import Image from "@components/Image/Image.svelte";
    import EmblaSlider from "@components/Slider/EmblaSlider.svelte";
    import EmblaSliderDots from "@components/Slider/EmblaSliderDots.svelte";
    import { EmblaSliderApi } from "@components/Slider/types";
    import VideoPlayer from "@components/VideoPlayer/VideoPlayer.svelte";

    import InstagramIcon from "../icons/InstagramIcon.svelte";
    import InstagramPostModalManager from "./InstagramPostModalManager";

    export let instagramPostModalManager: InstagramPostModalManager;
    export let post: InstagramPostEntity;
    export let selectedTagId: number | null = null;
    export let activeMediaTags: InstagramPostTagEntity[];
    export let highQualityEnabled: boolean;

    const { productsQuery } = getTypedContext(
        instagramGalleryWidgetContextToken
    );

    const dispatch = createEventDispatcher();
    const handleTagEnter = (id: number) => {
        dispatch("tag_select", id);
    };
    const handleTagLeave = () => {
        dispatch("tag_unselect");
    };
    let sliderApi: EmblaSliderApi | null = null;
    let currentIndex = 0;
    let slidesCount = 0;

    $: {
        currentIndex;
        // Update media ID when currentIndex changes
        if (post.media[currentIndex]) {
            instagramPostModalManager.changeMediaId(
                post.media[currentIndex].id
            );
        }
    }

    function handleSelect() {
        if (sliderApi) {
            currentIndex = sliderApi.selectedScrollSnap();
            if (!sliderApi.canScrollNext()) {
                dispatch("loadMore");
            }
        }
    }

    function handleSliderReady() {
        if (!sliderApi) return;

        slidesCount = post.media.length;
        currentIndex = sliderApi.selectedScrollSnap();
    }

    const handlePrev = (): void => {
        sliderApi?.scrollPrev();
    };

    const handleNext = (): void => {
        sliderApi?.scrollNext();
    };
</script>

<div class="gw-ig-post-modal-viewer">
    {#each activeMediaTags as tag}
        <div
            style={`
                left: ${tag.coordinateX * 100}%;
                top: ${tag.coordinateY * 100}%;
                transform:
                    translate(
                        -${tag.coordinateX * 100}%,
                        -${tag.coordinateY * 100}%
                )`}
            class="gw-ig-post-modal-viewer__tag-wrapp"
            data-tag-id={tag.id}
            data-tagged-id={selectedTagId}
            class:gw-ig-post-modal-viewer__tag--not-selected={tag.id !==
                selectedTagId && selectedTagId !== null}
            aria-label=""
        >
            <div
                class="gw-ig-post-modal-viewer__tag"
                on:mouseenter={() => handleTagEnter(tag.id)}
                on:mouseleave={handleTagLeave}
            />

            {#if tag.type === "product"}
                <Observable
                    observable={createProductInfoObservable(
                        productsQuery,
                        tag.product.id,
                        tag.variantId
                    )}
                    let:value
                >
                    {#if value.status === STATUS.READY}
                        <TagTooltip
                            {tag}
                            {selectedTagId}
                            price={value.variant?.price?.amount ||
                                value.product.variants[0].price.amount}
                        />
                    {/if}
                </Observable>
            {:else}
                <TagTooltip {tag} {selectedTagId} />
            {/if}
        </div>
    {/each}
    <EmblaSlider
        setApi={(api) => (sliderApi = api)}
        perPage={1}
        loop={false}
        controls={true}
        on:select={handleSelect}
        on:ready={handleSliderReady}
    >
        <svelte:fragment slot="slides">
            {#each post.media as image, index (image.id)}
                <div class="gw-ig-post-modal-viewer__media">
                    {#if image.type === INSTAGRAM_POST_MEDIA_VIDEO_TYPE && image.videoUrl}
                        <VideoPlayer
                            sources={[
                                {
                                    src: image.videoUrl,
                                    mime: "video/mp4",
                                },
                            ]}
                            poster={image.imageLarge}
                            uiPannelAutoHide={true}
                            showUIPanel={slidesCount <= 1}
                        />
                    {:else}
                        <Image
                            alt={post.caption || ""}
                            wrapperClassName="gw-ig-post-modal-viewer__img"
                            objectFit="contain"
                            src={highQualityEnabled
                                ? image.imageLarge
                                : image.imageMedium}
                        >
                            <Icon
                                slot="backdrop"
                                class="gw-ig-post-modal-viewer__icon"
                            >
                                <InstagramIcon />
                            </Icon>
                        </Image>
                    {/if}
                    <Clearfix />
                </div>
            {/each}
        </svelte:fragment>

        <svelte:fragment slot="arrows" let:hasPrev let:hasNext>
            <div class="gw-ig-post-modal-viewer__arrows">
                {#if slidesCount > 1}
                    {#if hasPrev}
                        <BaseButton
                            ariaLabel="prev instagram slide"
                            class="gw-ig-post-modal-viewer__arrow gw-ig-post-modal-viewer__arrow--prev"
                            on:click={handlePrev}
                            tabindex="0"
                        >
                            <Icon width={12} height={12}>
                                <ArrowIcon />
                            </Icon>
                        </BaseButton>
                    {/if}
                    {#if hasNext}
                        <BaseButton
                            ariaLabel="next instagram slide"
                            class="gw-ig-post-modal-viewer__arrow gw-ig-post-modal-viewer__arrow--next"
                            on:click={handleNext}
                            tabindex="0"
                        >
                            <Icon width={12} height={12}>
                                <ArrowIcon />
                            </Icon>
                        </BaseButton>
                    {/if}
                {/if}
            </div>
        </svelte:fragment>

        <EmblaSliderDots
            slot="dots"
            perPage={1}
            {slidesCount}
            api={sliderApi || undefined}
            class="gw-ig-post-modal-viewer__dots"
        />
    </EmblaSlider>
</div>

<style lang="postcss">
    .gw-ig-post-modal-viewer {
        position: relative;
        width: 100%;
        height: 100%;
    }

    .gw-ig-post-modal-viewer :global(.gw-ig-post-modal-viewer__img) {
        display: block;
        width: 100%;
    }

    :global(.gw-image.gw-ig-post-modal-viewer__img .gw-image__backdrop) {
        background-color: var(--gw-black-bg);
    }

    .gw-ig-post-modal-viewer :global(.gw-ig-post-modal-viewer__icon) {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100px;
        height: 100px;
        transform: translateX(-50%) translateY(-50%);
        color: #ccc;
    }

    .gw-ig-post-modal-viewer__tag-wrapp {
        display: flex;
        position: absolute;
        z-index: 2;
        align-items: center;
        justify-content: center;
    }

    .gw-ig-post-modal-viewer__tag-wrapp:hover {
        z-index: 3;
    }

    .gw-ig-post-modal-viewer__tag {
        display: block;
        position: relative;
        z-index: 1;
        width: 13px;
        height: 13px;
        transform: scale(1);
        animation: pulse 2s 5s infinite;
        border-radius: 50%;
        background: var(--gw-tooltip-bg);
        box-shadow: 0 0 0 0 rgb(0, 0, 0, 1);
        cursor: pointer;
    }

    .gw-ig-post-modal-viewer__tag--not-selected {
        opacity: 0.6;
    }

    .gw-ig-post-modal-viewer:hover .gw-ig-post-modal-viewer__tag {
        opacity: 1;
    }

    .gw-ig-post-modal-viewer__media {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
    }

    :global(.gw-slider__slides) {
        .gw-ig-post-modal-viewer__arrows {
            height: auto;
        }
    }

    .gw-ig-post-modal-viewer__arrows :global(.gw-ig-post-modal-viewer__arrow) {
        display: flex;
        position: absolute;
        z-index: 3;
        bottom: 50%;
        padding: 10px;
        border-radius: 50%;
        background: var(--gw-grey-close-btn-bg);
        backdrop-filter: blur(8px);

        &:hover {
            opacity: 1;
        }

        & :global(svg) {
            stroke: var(--gw-grey-reset-button-color);

            & :global(path) {
                stroke-width: 6px;
            }
        }
    }

    :global(.gw-ig-post-modal-viewer__arrow--prev) {
        left: 32px;
        transform: rotate(180deg);
    }

    :global(.gw-ig-post-modal-viewer__arrow--next) {
        right: 32px;
    }

    :global(.gw-ig-post-modal-viewer__dots) {
        position: absolute;
        z-index: 3;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%);
    }

    :global(.gw-ig-post-modal-viewer__dots) :global(.gw-dots__button) {
        opacity: 0.6;
        color: var(--gw-white-text-color);
    }

    :global(.gw-ig-post-modal-viewer__dots) :global(.gw-dots__button--active) {
        transform: scale(1.4);
        opacity: 1;
    }

    /* TODO: придумать более хороший способ задавать размер */
    .gw-ig-post-modal-viewer :global(.gw-slider__slides > div),
    .gw-ig-post-modal-viewer :global(.gw-slider__slides > div > div),
    .gw-ig-post-modal-viewer__media :global(.gw-video-player),
    .gw-ig-post-modal-viewer__media :global(.gw-video-player__video) {
        height: 100vh;
    }

    @keyframes pulse {
        0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 2px rgba(34, 33, 33, 0.9);
        }

        50% {
            transform: scale(0.95);
            box-shadow: 0 0 0 10px rgba(201, 196, 196, 0);
        }

        100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
        }
    }

    @media all and (max-width: 425px) {
        :global(.gw-ig-post-modal-viewer__arrow--prev) {
            left: 16px;
        }
        :global(.gw-ig-post-modal-viewer__arrow--next) {
            right: 16px;
        }
    }
</style>
